import { API_BASE_URL } from "@/lib/constants/app";
import { rateLimit } from "@/lib/utils/rateLimiter";
import axios from "axios";
import { BaseTool } from "./BaseTool.js";

/**
 * Record token usage for web search using actual API response data
 */
const recordWebSearchTokenUsage = async (
  userId,
  workspaceId,
  actualUsage,
  query,
  modelId = "gpt-4o-search-preview"
) => {
  try {
    // Import the token stats module
    const userTokenStatsModule = await import(
      "@/lib/db/utils/supabase/userTokenStats"
    );
    const userTokenStats = userTokenStatsModule.default;

    // Extract token details from OpenAI response format
    const promptTokens = actualUsage.prompt_tokens || 0;
    const completionTokens = actualUsage.completion_tokens || 0;
    const totalTokens = actualUsage.total_tokens || 0;

    // Calculate cost based on gpt-4o-search-preview pricing
    // Assuming similar pricing to gpt-4o: $2.50 per 1M input tokens, $10 per 1M output tokens
    const inputCost = (promptTokens / 1000000) * 2.5;
    const outputCost = (completionTokens / 1000000) * 10;
    const estimatedCost = inputCost + outputCost;

    // Record the token usage
    const result = await userTokenStats.create({
      userId,
      workspaceId,
      promptTokens,
      completionTokens,
      totalTokens,
      modelId,
      featureId: "web_search",
      metadata: {
        estimatedCost,
        query: query?.substring(0, 100) + "...", // Store truncated query for reference
        tracked_via: "web_search_tool",
        actualUsageData: actualUsage, // Store the full actual usage object
        timestamp: new Date().toISOString(),
      },
    });

    return result;
  } catch (error) {
    console.error("Failed to record web search token usage:", error);
    return null;
  }
};

/**
 * Performs a web search using the new API route
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} - The search results
 */
export const webSearch = async (params) => {
  const {
    query,
    limit = 10,
    searchContextSize = "medium",
    userLocation,
  } = params;

  try {
    // Apply rate limiting
    await rateLimit("web_search", {
      maxRequests: 50,
      perMinute: true,
    });

    // Validate inputs
    if (!query) {
      return {
        error: true,
        message: "No search query provided",
        timestamp: new Date().toISOString(),
      };
    }

    // Get the API base URL from environment or use default
    // const apiBaseUrl = import.meta.env.VITE_BASE_API_URL || "http://localhost:3002";
    const apiBaseUrl = API_BASE_URL;

    // Get user and workspace IDs from auth store
    let userId = null;
    let workspaceId = null;

    try {
      // Dynamically import auth store to avoid circular dependencies
      const { useAuthStore } = await import("@/store/authStore");
      const authState = useAuthStore.getState();

      userId = authState.userId || authState.user?.id;
      workspaceId = authState.currentWorkspaceId;
    } catch (authError) {
      console.warn(
        "Could not retrieve auth data for token tracking:",
        authError
      );
    }

    // Call the new web search API route
    const response = await axios.post(`${apiBaseUrl}/api/ai/tools/web-search`, {
      query,
      limit,
      searchContextSize,
      userLocation,
    });

    // The API returns data in JSON-RPC format, extract the result
    const apiResult = response.data.result;

    if (!apiResult || !apiResult.data) {
      throw new Error("Invalid response format from web search API");
    }

    // Handle different response formats from different providers
    let searchResults;
    if (Array.isArray(apiResult.data)) {
      // Simple array format (most providers)
      searchResults = apiResult.data;
    } else if (
      apiResult.data.results &&
      Array.isArray(apiResult.data.results)
    ) {
      // Exa provider format with metadata
      searchResults = apiResult.data.results;
    } else {
      throw new Error("Invalid response format from web search API");
    }

    // Extract actual usage data from API response
    const actualUsage = apiResult.metadata?.usage || null;

    // Record token usage if we have user data and usage information
    if (userId && actualUsage) {
      await recordWebSearchTokenUsage(
        userId,
        workspaceId,
        actualUsage,
        query,
        apiResult.metadata?.model || "gpt-4o-search-preview"
      );
    } else {
      if (!userId) {
        console.warn("No user ID available, skipping token usage recording");
      }
      if (!actualUsage) {
        console.warn("No usage data available, skipping token usage recording");
      }
    }

    // Extract main content and sources - UPDATED for new structure without finalAnalysis
    const mainResult = searchResults[0];
    const sources = searchResults.slice(1);

    // Since finalAnalysis is removed, use the main result's description or combine source descriptions
    let mainContent = "";
    if (mainResult?.description) {
      mainContent = mainResult.description;
    } else if (sources.length > 0) {
      // If no main result description, create a summary from the sources
      mainContent = `Found ${sources.length} relevant sources for: "${query}"`;
    } else {
      mainContent = `Search completed for: "${query}"`;
    }

    // Format response to match expected tool format - UPDATED for data-only structure
    const formattedResult = {
      // Main content from the first data item or generated summary
      content: mainContent,
      markdown: mainContent,

      // Sources/citations from the remaining data items
      sources: sources.map((source) => ({
        url: source.url,
        title: source.title,
        description: source.description,
        metadata: source.metadata || {},
      })),

      // Metadata - UPDATED to reflect data-only structure
      metadata: {
        query,
        timestamp: new Date().toISOString(),
        provider: "langgraph_web_search", // Updated provider name
        model: apiResult.metadata?.model || "gpt-4.1-mini",
        totalSources: sources.length,
        usage: apiResult.metadata?.usage || {},
        qualityMetrics: apiResult.metadata?.qualityMetrics || {}, // Quality metrics
        hasMainResult: !!mainResult?.description,
        dataStructure: "array_only", // Indicate this is the new structure
        ...apiResult.metadata,
      },

      // Additional properties for compatibility - UPDATED for data-only structure
      data: searchResults,
      totalResults: searchResults.length,
      summary: mainResult, // Include the first data item as summary

      // Success indicator
      success: true,
      isError: false,
    };

    return formattedResult;
  } catch (error) {
    console.error(`Web search execution failed:`, error);

    return {
      error: true,
      message: error.message || `Failed to execute web search`,
      details: error.stack,
      timestamp: new Date().toISOString(),
      query,
      isError: true,
    };
  }
};

/**
 * Web search tool using the new API route
 */
export const webSearchTool = new BaseTool({
  id: "web_search",
  name: "Web Search",
  description:
    "Search the web for real-time information and return structured data array with sources",
  type: "function",
  category: "web",
  icon: "search",
  options: {
    rateLimit: { maxRequests: 10, perMinute: true },
  },
  runInstructions:
    "Use this tool to search the web for real-time information. Returns structured data array with search results and source citations for further analysis.",

  definition: {
    type: "function",
    function: {
      name: "web_search",
      description:
        "Search the web for real-time information and return structured data array with sources and citations.",
      parameters: {
        type: "object",
        properties: {
          query: {
            type: "string",
            description: "The search query",
          },
          limit: {
            type: "number",
            description: "Maximum number of results to return (default: 10)",
            default: 10,
          },
          searchContextSize: {
            type: "string",
            description:
              "Search context size - affects cost and quality (default: medium)",
            enum: ["low", "medium", "high"],
            default: "medium",
          },
          userLocation: {
            type: "object",
            description: "User location for geographically relevant results",
            properties: {
              country: {
                type: "string",
                description: "Two-letter ISO country code (e.g., 'US')",
              },
              city: {
                type: "string",
                description: "City name (e.g., 'New York')",
              },
              region: {
                type: "string",
                description: "Region/state name (e.g., 'California')",
              },
              timezone: {
                type: "string",
                description: "IANA timezone (e.g., 'America/New_York')",
              },
            },
          },
        },
        required: ["query"],
      },
    },
  },

  execute: webSearch,
});

export default webSearchTool;
